import { AppState, User } from '@/types';
import { apiService } from './api';
import { syncService } from './syncService';
import { notificationService } from './notificationService';
import { offlineStorageService } from './offlineStorage';

class AppStateService {
  private state: AppState = {
    isOnline: navigator.onLine,
    isAuthenticated: false,
    user: null,
    pendingSyncCount: 0,
  };

  private stateListeners: ((state: AppState) => void)[] = [];

  constructor() {
    this.initializeApp();
    this.setupEventListeners();
  }

  // Aggiungi listener per aggiornamenti stato
  addStateListener(listener: (state: AppState) => void): void {
    this.stateListeners.push(listener);
  }

  // Rimuovi listener
  removeStateListener(listener: (state: AppState) => void): void {
    this.stateListeners = this.stateListeners.filter(l => l !== listener);
  }

  // Notifica aggiornamenti stato a tutti i listener
  private notifyStateChange(): void {
    this.stateListeners.forEach(listener => listener({ ...this.state }));
  }

  // Ottieni stato corrente
  getState(): AppState {
    return { ...this.state };
  }

  // Inizializza app
  private async initializeApp(): Promise<void> {
    try {
      // Verifica autenticazione esistente
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const user = await apiService.getProfile();
          this.updateState({ isAuthenticated: true, user });
        } catch (error) {
          // Token non valido, rimuovi
          apiService.logout();
          this.updateState({ isAuthenticated: false, user: null });
        }
      }

      // Verifica connessione
      await this.checkConnection();

      // Aggiorna conteggio sincronizzazioni pendenti
      await this.updatePendingSyncCount();

    } catch (error) {
      console.error('App initialization failed:', error);
    }
  }

  // Setup event listeners
  private setupEventListeners(): void {
    // Online/Offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Visibility change (per sincronizzazione quando l'app torna in primo piano)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Before unload (per salvare dati prima di chiudere)
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));

    // Service Worker events
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage.bind(this));
    }
  }

  // Gestione evento online
  private async handleOnline(): Promise<void> {
    this.updateState({ isOnline: true });
    notificationService.onlineMode();

    // Sincronizzazione automatica quando torna online
    try {
      await syncService.autoSync();
      await this.updatePendingSyncCount();
    } catch (error) {
      console.warn('Auto sync failed:', error);
    }
  }

  // Gestione evento offline
  private handleOffline(): void {
    this.updateState({ isOnline: false });
    notificationService.offlineMode();
  }

  // Gestione cambio visibilità
  private async handleVisibilityChange(): Promise<void> {
    if (!document.hidden && this.state.isOnline) {
      // App tornata in primo piano e online, verifica sincronizzazioni
      try {
        await this.updatePendingSyncCount();
        await syncService.autoSync();
      } catch (error) {
        console.warn('Background sync failed:', error);
      }
    }
  }

  // Gestione before unload
  private handleBeforeUnload(): void {
    // Salva eventuali dati non salvati
    // (implementare se necessario)
  }

  // Gestione messaggi Service Worker
  private handleServiceWorkerMessage(event: MessageEvent): void {
    const { type } = event.data;

    switch (type) {
      case 'SYNC_COMPLETED':
        this.updatePendingSyncCount();
        break;
      case 'UPDATE_AVAILABLE':
        notificationService.updateAvailable();
        break;
      default:
        break;
    }
  }

  // Verifica connessione
  private async checkConnection(): Promise<void> {
    try {
      const isOnline = await apiService.isOnline();
      this.updateState({ isOnline });
    } catch (error) {
      this.updateState({ isOnline: false });
    }
  }

  // Aggiorna conteggio sincronizzazioni pendenti
  private async updatePendingSyncCount(): Promise<void> {
    try {
      const stats = await syncService.getOfflineStats();
      this.updateState({ 
        pendingSyncCount: stats.pendingDonations + stats.pendingReceipts 
      });
    } catch (error) {
      console.warn('Failed to update pending sync count:', error);
    }
  }

  // Aggiorna stato
  private updateState(updates: Partial<AppState>): void {
    this.state = { ...this.state, ...updates };
    this.notifyStateChange();
  }

  // Metodi pubblici per aggiornare stato
  setAuthenticated(user: User): void {
    this.updateState({ isAuthenticated: true, user });
  }

  setUnauthenticated(): void {
    this.updateState({ isAuthenticated: false, user: null });
  }

  // Logout completo con pulizia di tutti i dati
  async performLogout(): Promise<void> {
    try {
      // Pulisci token e dati di sessione
      apiService.logout();
      
      // Pulisci tutti i dati offline
      await offlineStorageService.clearAllData();
      
      // Reset completo del servizio geolocalizzazione
      const { geolocationService } = await import('./geolocationService');
      geolocationService.reset();
      
      // Reset servizio notifiche
      notificationService.reset();
      
      // Reset stato app
      this.updateState({ 
        isAuthenticated: false, 
        user: null,
        pendingSyncCount: 0
      });
      
      console.log('Logout completato: tutti i dati sono stati puliti');
    } catch (error) {
      console.error('Errore durante il logout:', error);
      // Anche se c'è un errore, assicurati che lo stato sia resettato
      this.updateState({ isAuthenticated: false, user: null });
    }
  }

  async refreshPendingSyncCount(): Promise<void> {
    await this.updatePendingSyncCount();
  }

  async refreshConnectionStatus(): Promise<void> {
    await this.checkConnection();
  }

  // Metodi di utilità
  isUserFieldAgent(): boolean {
    return this.state.user?.role === 'FIELD_AGENT';
  }

  canCreateDonations(): boolean {
    return this.state.isAuthenticated && (
      this.state.user?.role === 'FIELD_AGENT' ||
      this.state.user?.role === 'SUPERVISOR' ||
      this.state.user?.role === 'ADMIN'
    );
  }

  canManageDonors(): boolean {
    return this.state.isAuthenticated && (
      this.state.user?.role === 'FIELD_AGENT' ||
      this.state.user?.role === 'SUPERVISOR' ||
      this.state.user?.role === 'ADMIN'
    );
  }

  // Cleanup
  destroy(): void {
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', this.handleServiceWorkerMessage.bind(this));
    }
  }
}

export const appStateService = new AppStateService();
export default appStateService;
